<script setup lang="ts">
/**
 * Text with the parts that match a search marked — for result lists, tree
 * filters and search snippets. Renders real `<mark>` elements (no `v-html`),
 * so the text is never parsed as markup. Accent- and case-insensitive by
 * default: „mull“ marks „Müll“.
 *
 * `query` is a string (its words are marked each on their own), a list of
 * strings, or the terms of a text query (`TextQueryTerm[]`, `lib/textQuery`):
 * those mark the way the search matches them — `mustNot` and disabled terms
 * stay unmarked, `startsWith` / `endsWith` mark at the start / end of a word,
 * `phrase` takes white space as one space, and every term has its own case
 * flag (`caseSensitive` is for strings only). With `fold="spelled"` the marks
 * are exactly the ranges of the search: „Müller“ is marked for `mueller`.
 *
 * `ranges` marks half-open UTF-16 spans the caller already has (a search
 * snippet's own ranges). While it is an array, `query` is ignored. Omit it,
 * or pass `null`, and the component marks `query` as it always has.
 */
import { computed } from 'vue';
import type { TextQueryTerm } from '../lib/textQuery';
import {
  excerptSegments,
  splitHighlight,
  splitHighlightRanges,
  type NxHighlightOptions,
} from './highlight';

const props = withDefaults(
  defineProps<{
    text?: string | null;
    /** The search: words are marked each on their own, a list of strings, or the terms of a text query. */
    query?: string | readonly string[] | readonly TextQueryTerm[] | null;
    match?: NxHighlightOptions['match'];
    minLength?: number;
    /** For a string query or a list of strings; a `TextQueryTerm` has its own flag. Default `false`. */
    caseSensitive?: boolean;
    /** „Mull“ also marks „Müll“ — part of the `accents` fold. Default `true`. */
    ignoreDiacritics?: boolean;
    /**
     * `accents`: case and accents fall away (`ü` is `u`). `spelled`: the fold of
     * the search, `ü` is `ue` and `ß` is `ss`, with exactly its ranges. Default `accents`.
     */
    fold?: NxHighlightOptions['fold'];
    /** At most this many terms mark (terms that cannot mark do not count). Default 10. */
    maxTerms?: number;
    /** Wrapper element. Default `span`. */
    tag?: string;
    /**
     * Start shortly before the first hit instead of at the beginning — for a
     * long text the caller clamps to a few lines (a search snippet), so the hit
     * is in view. „… “ marks the cut; without a hit the text is unchanged.
     * Default `false`.
     */
    excerpt?: boolean;
    /** Characters kept before the first hit when `excerpt` is on. Default 24. */
    context?: number;
    /**
     * Half-open UTF-16 ranges `[start, end)` of `text` to mark. When this is
     * an array, those ranges are marked and `query` is ignored. A range that
     * is not a finite integer, that lies outside the text, or whose start is
     * not before its end is dropped; the rest are sorted, and overlapping or
     * touching ranges merge. `excerpt` cuts before the first of them the same
     * way it cuts before the first hit. Omit it, or pass `null`, to mark
     * `query` as before. Default `null`.
     */
    ranges?: readonly (readonly [number, number])[] | null;
  }>(),
  {
    text: '',
    query: '',
    match: 'words',
    minLength: 1,
    caseSensitive: false,
    ignoreDiacritics: true,
    fold: 'accents',
    maxTerms: 10,
    tag: 'span',
    excerpt: false,
    context: 24,
    ranges: null,
  }
);

const segments = computed(() => {
  const text = props.text ?? '';
  // An array — empty included — is the opt-in. `null` and omitting it keep the query.
  const all =
    props.ranges != null
      ? splitHighlightRanges(text, props.ranges)
      : splitHighlight(text, props.query ?? '', {
          match: props.match,
          minLength: props.minLength,
          caseSensitive: props.caseSensitive,
          ignoreDiacritics: props.ignoreDiacritics,
          fold: props.fold,
          maxTerms: props.maxTerms,
        });
  return props.excerpt ? excerptSegments(all, props.context) : all;
});
</script>

<template>
  <component :is="props.tag" class="nx-highlight" data-testid="nx-highlight">
    <template v-for="(segment, i) in segments" :key="i">
      <mark v-if="segment.hit" class="nx-highlight__mark">{{ segment.text }}</mark>
      <template v-else>{{ segment.text }}</template>
    </template>
  </component>
</template>

<style scoped>
.nx-highlight__mark {
  padding: 0;
  border-radius: calc(var(--nxt-hairline) * 2);
  /* A hit must stand out from the text around it in both themes. */
  background: color-mix(in srgb, var(--nxt-warning-bg) 38%, transparent);
  box-shadow: 0 0 0 calc(var(--nxt-hairline) * 1.5)
    color-mix(in srgb, var(--nxt-warning-bg) 38%, transparent);
  color: inherit;
  font-weight: var(--nxt-font-weight-semibold);
}

@media (forced-colors: active) {
  .nx-highlight__mark {
    background: Mark;
    color: MarkText;
  }
}
</style>
